import { useState, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import { promptAndSetShoppingReminder } from '../utils/shoppingReminder';

/**
 * [R5] useKitchenPlan — Diyet & Sporcu mutfaklarının ORTAK plan/swap/alışveriş/ufuk mantığı.
 *
 * DietKitchen & AthleteKitchen ~%80 aynı state+handler taşıyordu (B14: fetch, horizon modalı,
 * swap, alışveriş listesi). Tek kaynak burada; sayfa sadece render + config'e odaklanır.
 *
 * @param {'diet'|'athlete'} mode
 * @param {Object} opts
 * @param {(data:object)=>void} opts.onPlanLoaded  plan yüklenince sayfa-özel işlem (athlete: gün seçimi)
 * @param {string} opts.confirmColor  7g alışveriş modalı onay rengi (diet yeşil / athlete turuncu)
 *
 * Not: Plan fetch ARTIK otomatik değil — sayfa açıkça fetchWeeklyPlan() çağırır (initialize),
 * ya da kullanıcı ufuk modalı/toggle ile seçer (chooseHorizon/onHorizonChange). Böylece günlük
 * seçilince haftalık HİÇ hesaplanmaz (plan seçimin arkasından oluşur).
 */
export default function useKitchenPlan(mode, { onPlanLoaded = null, confirmColor = '#16a34a' } = {}) {
    const storageKey = `${mode}_horizon`;

    const [planningHorizon, setPlanningHorizon] = useState(() => {
        try { const v = localStorage.getItem(storageKey); return (v === 'today' || v === '7d') ? v : 'today'; } catch { return 'today'; }
    });
    const [showHorizonModal, setShowHorizonModal] = useState(() => {
        try { return !localStorage.getItem(storageKey); } catch { return false; }
    });
    const [weekPlan, setWeekPlan] = useState([]);
    const [stockMode, setStockMode] = useState('block');
    const [shoppingPayload, setShoppingPayload] = useState(null);
    const [shoppingItemCount, setShoppingItemCount] = useState(0);
    const [creatingShoppingList, setCreatingShoppingList] = useState(false);
    const [swapState, setSwapState] = useState({ open: false, date: null, mealType: null, current: null, loading: false, applying: false, alternatives: [] });
    const [selectedRecipe, setSelectedRecipe] = useState(null);
    const [recipeModalOpen, setRecipeModalOpen] = useState(false);
    const [selectedPortion, setSelectedPortion] = useState(1);

    // Kullanıcı 7 güne BASINCA alışveriş listesi sor (otomatik yüklemede sorma).
    const pendingShopPromptRef = useRef(false);

    const persistHorizon = (v) => { setPlanningHorizon(v); try { localStorage.setItem(storageKey, v); } catch (_e) { /* no-op */ } };
    // [R5-fix] Plan SEÇİMDEN SONRA oluşur: günlük seçilince haftalık HİÇ hesaplanmaz (defer fetch).
    // Modal seçimi/toggle her zaman açıkça fetch eder (değer aynı olsa bile), useEffect'e gerek yok.
    const chooseHorizon = (v) => { if (v === '7d') pendingShopPromptRef.current = true; persistHorizon(v); setShowHorizonModal(false); fetchWeeklyPlan(v); };
    const onHorizonChange = (v) => { if (v === '7d' && v !== planningHorizon) pendingShopPromptRef.current = true; persistHorizon(v); fetchWeeklyPlan(v); };

    const fetchWeeklyPlan = async (horizonOverride) => {
        try {
            const token = localStorage.getItem('authToken');
            const horizon = horizonOverride || planningHorizon || 'today';
            const res = await axios.get(`${API_BASE_URL}/api/${mode}/weekly-plan`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { horizon }
            });
            if (res.data.success) {
                setWeekPlan(res.data.weekPlan || []);
                setStockMode(res.data.stockMode || 'block');
                setShoppingItemCount(res.data.shoppingItemCount || 0);
                setShoppingPayload(res.data.shoppingListPayload || null);
                if (onPlanLoaded) onPlanLoaded(res.data); // sayfa-özel (athlete gün seçimi)
                if (pendingShopPromptRef.current) {
                    pendingShopPromptRef.current = false;
                    await promptCreateShoppingForWeekly(res.data);
                }
                return res.data;
            }
        } catch (err) {
            console.error(`${mode} weekly plan fetch error:`, err);
        }
        return null;
    };

    const promptCreateShoppingForWeekly = async (data) => {
        const count = data?.shoppingItemCount || 0;
        if (data?.stockMode !== 'shop' || count <= 0) return;
        const r = await Swal.fire({
            title: '7 Günlük Plan Hazır 🗓️',
            html: `Bu 7 günlük menüyü tamamlamak için dolabında olmayan <b>${count} malzeme</b> var.<br/>Bunları alışveriş listene ekleyelim mi?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Evet, listeyi oluştur',
            cancelButtonText: 'Hayır, devam et',
            confirmButtonColor: confirmColor,
            reverseButtons: true,
            // [R5-fix] Dışarı tıkla/ESC ile kapanmasın — kullanıcı Evet/Hayır seçmeli.
            allowOutsideClick: false,
            allowEscapeKey: false
        });
        if (r.isConfirmed) await handleCreateShoppingList(data.shoppingListPayload);
    };

    const handleCreateShoppingList = async (payloadOverride) => {
        const payload = payloadOverride && payloadOverride.items ? payloadOverride : shoppingPayload;
        if (!payload || !payload.items?.length) return;
        setCreatingShoppingList(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.post(`${API_BASE_URL}/api/shopping/lists`, {
                name: payload.name,
                items: payload.items
            }, { headers: { Authorization: `Bearer ${token}` } });
            const warnings = Array.isArray(res.data?.warnings) ? res.data.warnings.length : 0;
            await Swal.fire({
                title: 'Alışveriş Listesi Oluşturuldu!',
                html: `<b>${payload.name}</b><br/>${payload.items.length} kalem Market Listelerim'e eklendi.` +
                    (warnings ? `<br/><span style="color:#d97706;font-size:12px">${warnings} ürün manuel eşleme bekliyor.</span>` : ''),
                icon: 'success',
                confirmButtonText: 'Tamam'
            });
            // Liste hazır → "hatırlatma kuralım mı?" ikinci modalı (push). Haftalık alışveriş için hayat kurtarıcı.
            const newListId = res.data?.id;
            if (newListId) {
                await promptAndSetShoppingReminder(newListId, payload.name, { confirmColor });
            }
        } catch (err) {
            console.error('Shopping list create error:', err);
            Swal.fire('Hata', 'Alışveriş listesi oluşturulamadı.', 'error');
        } finally {
            setCreatingShoppingList(false);
        }
    };

    // Mevcut plandaki tüm slotları lock'a çevir (swap'ta diğerleri SABİT kalsın).
    const buildLocksFromPlan = (plan) => {
        const locks = {};
        (plan || []).forEach((day) => {
            if (!day?.date) return;
            const slot = {};
            ['breakfast', 'lunch', 'dinner'].forEach((m) => {
                const r = (day.meals?.[m] || [])[0];
                if (r) slot[m] = String(r.id);
            });
            if (Object.keys(slot).length) locks[day.date] = slot;
        });
        return locks;
    };

    const openSwap = async (date, mealType, recipe) => {
        setSwapState({ open: true, date, mealType, current: recipe, loading: true, applying: false, alternatives: [] });
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/${mode}/meal-alternatives`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { date, mealType, excludeId: recipe?.id, horizon: planningHorizon }
            });
            setSwapState((s) => ({ ...s, loading: false, alternatives: res.data?.alternatives || [] }));
        } catch (err) {
            console.error('Meal alternatives error:', err);
            setSwapState((s) => ({ ...s, loading: false, alternatives: [] }));
        }
    };

    const applySwap = async (altId) => {
        const { date, mealType } = swapState;
        const prevCount = shoppingItemCount;
        const locks = buildLocksFromPlan(weekPlan);
        if (!locks[date]) locks[date] = {};
        locks[date][mealType] = String(altId);
        setSwapState((s) => ({ ...s, applying: true }));
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.post(`${API_BASE_URL}/api/${mode}/recompute-plan`,
                { lockedSlots: locks, horizon: planningHorizon },
                { headers: { Authorization: `Bearer ${token}` } });
            if (res.data?.success) {
                setWeekPlan(res.data.weekPlan || []);
                setStockMode(res.data.stockMode || 'block');
                setShoppingItemCount(res.data.shoppingItemCount || 0);
                setShoppingPayload(res.data.shoppingListPayload || null);
            }
            setSwapState({ open: false, date: null, mealType: null, current: null, loading: false, applying: false, alternatives: [] });
            const newCount = res.data?.shoppingItemCount || 0;
            if (newCount > prevCount) {
                const r = await Swal.fire({
                    title: 'Değişiklik uygulandı',
                    html: `Bu değişiklik alışveriş listene <b>${newCount - prevCount}</b> kalem ekledi (toplam ${newCount}). Listeyi oluşturmak ister misin?`,
                    icon: 'info',
                    showCancelButton: true,
                    confirmButtonText: 'Alışveriş Listesi Oluştur',
                    cancelButtonText: 'Sonra'
                });
                if (r.isConfirmed) await handleCreateShoppingList(res.data.shoppingListPayload);
            }
        } catch (err) {
            console.error('Recompute plan error:', err);
            setSwapState((s) => ({ ...s, applying: false }));
            Swal.fire('Hata', 'Plan güncellenemedi.', 'error');
        }
    };

    const handleRecipeClick = async (recipe) => {
        // Kartın gösterdiği composer porsiyonunu modala da taşı (malzeme+fiyat aynı ölçeklensin).
        const portion = Number(recipe?.mealPortion) > 0
            ? Number(recipe.mealPortion)
            : (Number(recipe?.targetPortion) > 0 ? Number(recipe.targetPortion) : 1);
        setSelectedPortion(portion);
        setSelectedRecipe(await hydrateRecipeForModal(recipe, { targetPortion: portion }));
        setRecipeModalOpen(true);
    };

    return {
        // state
        planningHorizon, showHorizonModal, setShowHorizonModal,
        weekPlan, setWeekPlan, stockMode, shoppingPayload, shoppingItemCount, creatingShoppingList,
        swapState, setSwapState, selectedRecipe, recipeModalOpen, setRecipeModalOpen, selectedPortion,
        // handlers
        chooseHorizon, onHorizonChange, fetchWeeklyPlan,
        handleCreateShoppingList, openSwap, applySwap, handleRecipeClick
    };
}
